import { ClipboardList } from "lucide-react";

import { Label } from "components/ui/label";

import type { FeedbackForm } from "../../feedback/schemas";
import { findForm, pickerWarning } from "../announcement-form";
import { WARNING_BOX } from "./WarningNote";

export const NO_FORM = "";

/**
 * Links one of the event's feedback forms to an announcement, so the
 * notification carries a button that opens it.
 *
 * A native select: on a phone it opens the platform picker, which is easier
 * to hit than a custom list. The warning below judges the form at the moment
 * the announcement would go out (now, or the chosen send time), because that
 * is when the backend decides whether to send it.
 *
 * Renders nothing while the event has no forms and none is chosen, so an
 * event that does not use feedback sees no extra field.
 */
export function FeedbackFormPicker({
  id,
  testId,
  forms,
  value,
  onChange,
  mode,
  at,
  timeZone,
  disabled,
}: {
  id: string;
  testId: string;
  /** null while loading. */
  forms: FeedbackForm[] | null;
  /** A form id, or NO_FORM. */
  value: string;
  onChange: (formId: string) => void;
  mode: "now" | "later";
  /** When it would go out: now, or the chosen send time (null if none yet). */
  at: Date | null;
  timeZone: string | undefined;
  disabled?: boolean;
}) {
  if ((forms === null || forms.length === 0) && value === NO_FORM) return null;
  const chosen = value === NO_FORM ? undefined : findForm(forms, value);
  const known = value === NO_FORM || chosen !== undefined || forms === null;
  const warning =
    value === NO_FORM || forms === null
      ? null
      : pickerWarning(chosen, mode, at, timeZone);
  const hintId = `${id}-hint`;

  return (
    <div className="space-y-1.5">
      <Label htmlFor={id} className="flex items-center gap-2">
        <ClipboardList className="h-4 w-4 shrink-0" />
        Feedback form
      </Label>
      <select
        id={id}
        data-testid={testId}
        value={value}
        disabled={disabled}
        aria-describedby={hintId}
        onChange={(event) => onChange(event.target.value)}
        className="min-h-11 w-full rounded-md border border-input bg-background px-3 text-base text-foreground disabled:cursor-not-allowed disabled:opacity-50 sm:min-h-9 sm:text-sm"
      >
        <option value={NO_FORM}>No feedback form</option>
        {!known && <option value={value}>Deleted form</option>}
        {(forms ?? []).map((form) => (
          <option key={form.id} value={form.id}>
            {form.title}
            {form.status === "open" ? "" : " (closed)"}
          </option>
        ))}
      </select>
      {warning ? (
        <p
          id={hintId}
          role="status"
          className={WARNING_BOX}
          data-testid={`${testId}-warning`}
        >
          {warning}
        </p>
      ) : (
        <p
          id={hintId}
          className="text-xs text-muted-foreground"
          data-testid={`${testId}-hint`}
        >
          {value === NO_FORM
            ? "Optional. The notification can carry a button that opens one of this event's feedback forms."
            : "The notification gets a button that opens this form."}
        </p>
      )}
    </div>
  );
}
